import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from '../utils/toast';
import CanadianPhoneInput from './CanadianPhoneInput';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  User,
  MapPin,
  Briefcase,
  CreditCard,
  Shield,
  Sparkles,
  Camera,
  Upload,
  Loader2,
  Phone,
  Mail,
  Clock,
  Star,
  FileText,
  CheckCircle,
  Image
} from 'lucide-react';
import '../styles/onboarding-wizard.css';
import { BACKEND_URL } from '../config/api';
import { useRecaptcha, RECAPTCHA_ACTIONS } from '../hooks/useRecaptcha';
import { buildSteps, calculateAge, getStepType as resolveStepType } from './onboarding/wizardConfig';
import StepIndicators from './onboarding/StepIndicators';
import WelcomeStep from './onboarding/WelcomeStep';

const API = BACKEND_URL;

/**
 * New User Onboarding Wizard
 * - Modal overlay that triggers on first login
 * - Skippable and resumable
 * - Different steps for Heroes vs Clients
 */
const OnboardingWizard = ({ isOpen, onClose, onComplete }) => {
  const { user, setUser } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { getRecaptchaToken } = useRecaptcha();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const idFileInputRef = useRef(null);
  const idCameraInputRef = useRef(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    date_of_birth: '',
    profilePhoto: null,
    profilePhotoPreview: null,
    location: null,
    address: '',
    // Role selection
    selectedRole: null, // 'client', 'hero', or 'both'
    // Hero specific
    bio: '',
    hourlyRate: '',
    skills: [],
    // ID verification
    idDocument: null,
    idDocumentPreview: null,
    // Terms agreement
    termsAccepted: false
  });

  // calculateAge is imported from ./onboarding/wizardConfig

  // Available skills/categories — fetched from API
  const [availableSkills, setAvailableSkills] = useState([]);
  
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/api/categories`);
        const cats = response.data.categories || [];
        setAvailableSkills(cats.map(c => ({
          id: c.key || c.name.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
          label: c.name,
          icon: c.icon || '📋'
        })));
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Role options imported from ./onboarding/wizardConfig (ROLE_OPTIONS)

  // Determine effective role (selected during onboarding or existing)
  const selectedRole = formData.selectedRole || (user?.role === 'task_hero' ? 'hero' : user?.role_preference) || null;
  const showHeroSteps = selectedRole === 'hero' || selectedRole === 'both';
  const showClientSteps = selectedRole === 'client' || selectedRole === 'both';

  // Build wizard step list + current step type
  const steps = buildSteps({ showHeroSteps });
  const totalSteps = steps.length;

  const currentStepType = resolveStepType(currentStep, steps);

  // Load saved progress on mount
  useEffect(() => {
    if (isOpen && user) {
      loadProgress();
    }
  }, [isOpen, user]);

  // Initialize Google Places autocomplete
  useEffect(() => {
    if (currentStepType === 'location') {
      // Use centralized loader
      const { loadGoogleMaps } = require('../utils/googleMaps');
      loadGoogleMaps(() => {
        initAutocomplete();
      });
    }
  }, [currentStepType]);

  const initAutocomplete = () => {
    const input = document.getElementById('onboarding-address-input');
    if (!input || !window.google) return;

    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      componentRestrictions: { country: 'ca' },
      fields: ['formatted_address', 'geometry', 'address_components']
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        setFormData(prev => ({
          ...prev,
          address: place.formatted_address,
          location: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            address: place.formatted_address
          }
        }));
      }
    });
  };

  const loadProgress = async () => {
    try {
      const response = await axios.get(`${API}/api/users/onboarding-progress`, {
        withCredentials: true
      });
      
      if (response.data) {
        const { step, data } = response.data;
        if (step && step > 1) {
          setCurrentStep(step);
        }
        if (data) {
          setFormData(prev => ({ ...prev, ...data }));
        }
      }
    } catch (error) {
      // No saved progress, start from beginning
    }
  };

  const saveProgress = async (step, data = {}) => {
    try {
      await axios.post(`${API}/api/users/onboarding-progress`, {
        step,
        data: { ...formData, ...data }
      }, { withCredentials: true });
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        profilePhoto: file,
        profilePhotoPreview: reader.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleIdUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Document must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        idDocument: file,
        idDocumentPreview: reader.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const toggleSkill = (skillId) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skillId)
        ? prev.skills.filter(s => s !== skillId)
        : [...prev.skills, skillId]
    }));
  };

  const selectRole = (roleId) => {
    setFormData(prev => ({
      ...prev,
      selectedRole: roleId
    }));
  };

  const validateStep = () => {
    if (currentStepType === 'welcome') {
      if (!formData.selectedRole) {
        toast.error('Please select how you want to use taskeri®');
        return false;
      }
      return true;
    }
    if (currentStepType === 'basic_info') {
      if (!formData.name?.trim()) {
        toast.error('Please enter your name');
        return false;
      }
      if (!formData.date_of_birth) {
        toast.error('Please enter your date of birth');
        return false;
      }
      // Age verification - must be 18+
      const age = calculateAge(formData.date_of_birth);
      if (age < 18) {
        toast.error('You must be 18 or older to use taskeri®');
        return false;
      }
      return true;
    }
    if (currentStepType === 'location') {
      if (!formData.location) {
        toast.error('Please select your location');
        return false;
      }
      return true;
    }
    if (currentStepType === 'skills') {
      if (formData.skills.length === 0) {
        toast.error('Please select at least one skill');
        return false;
      }
      return true;
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) return;

    setSaving(true);
    
    // Save role selection
    if (currentStepType === 'welcome' && formData.selectedRole) {
      try {
        await axios.put(`${API}/api/users/profile`, {
          role_preference: formData.selectedRole
        }, { withCredentials: true });
      } catch (error) {
        console.error('Failed to save role preference:', error);
      }
    }
    
    // Save data for current step
    if (currentStepType === 'basic_info' && formData.name) {
      try {
        const recaptchaToken = await getRecaptchaToken(RECAPTCHA_ACTIONS.UPDATE_PROFILE);
        await axios.put(`${API}/api/users/profile`, {
          name: formData.name,
          phone: formData.phone,
          date_of_birth: formData.date_of_birth,
          recaptcha_token: recaptchaToken
        }, { withCredentials: true });

        // Upload photo if selected
        if (formData.profilePhoto) {
          const photoFormData = new FormData();
          photoFormData.append('picture', formData.profilePhoto);
          await axios.post(`${API}/api/users/profile-picture`, photoFormData, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      } catch (error) {
        toast.error('Failed to save profile');
        setSaving(false);
        return;
      }
    }

    if (currentStepType === 'location' && formData.location) {
      try {
        const recaptchaToken = await getRecaptchaToken(RECAPTCHA_ACTIONS.UPDATE_PROFILE);
        await axios.put(`${API}/api/users/profile`, {
          location: formData.location,
          recaptcha_token: recaptchaToken
        }, { withCredentials: true });
      } catch (error) {
        toast.error('Failed to save location');
        setSaving(false);
        return;
      }
    }

    if (currentStepType === 'skills' && formData.skills.length > 0) {
      try {
        await axios.put(`${API}/api/users/hero-profile`, {
          skills: formData.skills,
          bio: formData.bio,
          hourly_rate: parseFloat(formData.hourlyRate) || 25
        }, { withCredentials: true });
      } catch (error) {
        toast.error('Failed to save skills');
        setSaving(false);
        return;
      }
    }

    await saveProgress(currentStep + 1);
    setSaving(false);
    
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = async () => {
    await saveProgress(currentStep + 1);
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleDismiss = async () => {
    try {
      await axios.post(`${API}/api/users/onboarding-dismiss`, {}, {
        withCredentials: true
      });
    } catch (error) {
      // Continue anyway
    }
    onClose();
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/users/onboarding-complete`, {}, {
        withCredentials: true
      });
      
      // Refresh user data
      const userResponse = await axios.get(`${API}/api/auth/me`, {
        withCredentials: true
      });
      if (userResponse.data) {
        setUser(userResponse.data);
      }
      
      toast.success('Welcome to taskeri®! 🎉');
      onComplete?.();
      onClose();
    } catch (error) {
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const handleStripeSetup = async () => {
    setLoading(true);
    try {
      // First create account if needed
      await axios.post(`${API}/api/connect/create-account`, {}, {
        withCredentials: true
      });
      
      // Then get onboarding link
      const response = await axios.post(`${API}/api/connect/onboarding-link`, {
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      // Save progress before redirect
      await saveProgress(currentStep);
      
      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start bank payout setup');
      setLoading(false);
    }
  };

  const handleIdSubmit = async () => {
    if (!formData.idDocument) {
      toast.error('Please upload your ID document');
      return;
    }

    setLoading(true);
    try {
      const idFormData = new FormData();
      idFormData.append('file', formData.idDocument);
      idFormData.append('document_type', 'government_id');
      
      await axios.post(`${API}/api/verification/upload`, idFormData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('ID uploaded for verification');
      handleNext();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload ID');
    } finally {
      setLoading(false);
    }
  };

  // Handle accepting terms
  const handleAcceptTerms = async () => {
    if (!formData.termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setSaving(true);
    try {
      // Save terms acceptance to backend
      await axios.post(`${API}/api/users/accept-terms`, {
        accepted_terms: true,
        accepted_privacy: true,
        accepted_at: new Date().toISOString()
      }, { withCredentials: true });
      
      handleNext();
    } catch (error) {
      console.error('Failed to save terms acceptance:', error);
      // Still proceed even if backend fails - terms were accepted locally
      handleNext();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const progress = ((currentStep - 1) / (totalSteps - 1)) * 100;

  return (
    <div className="onboarding-overlay" onClick={handleDismiss}>
      <div className="onboarding-modal" onClick={e => e.stopPropagation()}>
        {/* Close button */}
        <button className="onboarding-close" onClick={handleDismiss} title="Skip for now">
          <X size={20} />
        </button>

        {/* Progress bar */}
        <div className="onboarding-progress">
          <div className="progress-bar" style={{ width: `${progress}%` }} />
        </div>

        {/* Step indicators */}
        <StepIndicators steps={steps} currentStep={currentStep} />

        {/* Content area */}
        <div className="onboarding-content">
          {/* Step 1: Welcome & Role Selection */}
          {currentStepType === 'welcome' && (
            <WelcomeStep
              selectedRole={formData.selectedRole}
              onSelectRole={selectRole}
            />
          )}

          {/* Step 2: Basic Info */}
          {currentStepType === 'basic_info' && (
            <div className="step-content">
              <h2>Tell us about yourself</h2>
              <p className="step-subtitle">This helps others recognize you</p>

              {/* Profile Photo */}
              <div className="photo-upload-section">
                <div 
                  className="photo-preview"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {formData.profilePhotoPreview || user?.picture ? (
                    <img src={formData.profilePhotoPreview || user?.picture} alt="Profile" />
                  ) : (
                    <div className="photo-placeholder">
                      <Camera size={32} />
                      <span>Add Photo</span>
                    </div>
                  )}
                  <div className="photo-edit-badge">
                    <Camera size={14} />
                  </div>
                </div>
                <div className="photo-upload-buttons">
                  <button 
                    type="button"
                    className="photo-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Image size={16} />
                    Gallery
                  </button>
                  <button 
                    type="button"
                    className="photo-btn camera"
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    <Camera size={16} />
                    Camera
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  style={{ display: 'none' }}
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="user"
                  onChange={handlePhotoUpload}
                  style={{ display: 'none' }}
                />
              </div>

              {/* Name */}
              <div className="form-group">
                <label>Full Name *</label>
                <div className="input-with-icon">
                  <User size={18} />
                  <input
                    type="text"
                    value={formData.name || user?.name || ''}
                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your full name"
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="form-group">
                <label>Phone Number (optional)</label>
                <CanadianPhoneInput
                  value={formData.phone || ''}
                  onChange={(val) => setFormData(prev => ({ ...prev, phone: val }))}
                  showTypeSelector={true}
                  testIdPrefix="onboarding-phone"
                  placeholder="(416) 555-1234"
                />
              </div>

              {/* Date of Birth */}
              <div className="form-group">
                <label>Date of Birth *</label>
                <div className="input-with-icon">
                  <User size={18} />
                  <input
                    type="date"
                    value={formData.date_of_birth || ''}
                    onChange={e => setFormData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                    max={new Date().toISOString().split('T')[0]}
                    required
                    data-testid="onboarding-dob-input"
                  />
                </div>
                {formData.date_of_birth && calculateAge(formData.date_of_birth) >= 18 && (
                  <p className="input-hint" style={{ marginTop: '8px', color: '#047857', fontWeight: '500' }}>
                    <CheckCircle size={14} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                    Age verified: {calculateAge(formData.date_of_birth)} years old
                  </p>
                )}
                {formData.date_of_birth && calculateAge(formData.date_of_birth) < 18 && (
                  <p className="input-hint" style={{ marginTop: '8px', color: '#dc2626', fontWeight: '500' }}>
                    You must be 18 or older to use taskeri<sup style={{fontSize:'0.55em'}}>®</sup>
                  </p>
                )}
                {!formData.date_of_birth && (
                  <p className="input-hint" style={{ marginTop: '8px', color: '#64748b', fontSize: '12px' }}>
                    You must be at least 18 years old to use taskeri<sup style={{fontSize:'0.55em'}}>®</sup>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Location */}
          {currentStepType === 'location' && (
            <div className="step-content">
              <h2>Where are you located?</h2>
              <p className="step-subtitle">
                {showHeroSteps 
                  ? "We'll show you jobs within 40km of your location"
                  : "We'll connect you with heroes in your area"}
              </p>

              <div className="form-group">
                <label>Your Address *</label>
                <div className="input-with-icon">
                  <MapPin size={18} />
                  <input
                    id="onboarding-address-input"
                    type="text"
                    value={formData.address || ''}
                    onChange={e => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Start typing your address..."
                  />
                </div>
                <p className="input-hint">Your exact address is never shown to others</p>
              </div>

              {formData.location && (
                <div className="location-confirmed">
                  <Check size={16} />
                  <span>Location set: {formData.address}</span>
                </div>
              )}
            </div>
          )}

          {/* Skills Step (Hero only) */}
          {currentStepType === 'skills' && (
            <div className="step-content">
              <h2>What can you help with?</h2>
              <p className="step-subtitle">Select the services you offer</p>

              <div className="skills-grid">
                {availableSkills.map(skill => (
                  <button
                    key={skill.id}
                    className={`skill-chip ${formData.skills.includes(skill.id) ? 'selected' : ''}`}
                    onClick={() => toggleSkill(skill.id)}
                  >
                    <span className="skill-icon">{skill.icon}</span>
                    <span>{skill.label}</span>
                    {formData.skills.includes(skill.id) && <Check size={14} />}
                  </button>
                ))}
              </div>

              <div className="form-group" style={{ marginTop: '20px' }}>
                <label>Hourly Rate ($CAD)</label>
                <div className="input-with-icon">
                  <CreditCard size={18} />
                  <input
                    type="number"
                    value={formData.hourlyRate || ''}
                    onChange={e => setFormData(prev => ({ ...prev, hourlyRate: e.target.value }))}
                    placeholder="25"
                    min="15"
                    max="200"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Short Bio (optional)</label>
                <textarea
                  value={formData.bio || ''}
                  onChange={e => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Tell clients a bit about yourself and your experience..."
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Bank Payouts Step (Hero only) */}
          {currentStepType === 'payouts' && (
            <div className="step-content">
              <h2>Set Up Bank Payouts</h2>
              <p className="step-subtitle">Connect your bank account — earnings are transferred to your Stripe wallet and paid out to your bank daily</p>

              <div className="payout-info">
                <div className="payout-benefit">
                  <CreditCard size={24} />
                  <div>
                    <h4>Secure Bank Transfers</h4>
                    <p>Powered by Stripe, used by millions worldwide</p>
                  </div>
                </div>
                <div className="payout-benefit">
                  <Clock size={24} />
                  <div>
                    <h4>Daily Bank Payouts</h4>
                    <p>Stripe pays your wallet out to your bank automatically every day</p>
                  </div>
                </div>
              </div>

              <button 
                className="btn-stripe-setup"
                onClick={handleStripeSetup}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="spin" size={18} />
                    Connecting...
                  </>
                ) : (
                  <>
                    <CreditCard size={18} />
                    Set Up Bank Account
                  </>
                )}
              </button>

              <p className="skip-hint">
                You can also set this up later from your profile
              </p>
            </div>
          )}

          {/* Verify ID Step */}
          {currentStepType === 'verify_id' && (
            <div className="step-content">
              <h2>Verify Your Identity</h2>
              <p className="step-subtitle">
                Upload a government-issued ID to build trust
              </p>

              <div className="id-upload-section">
                <div className="id-upload-buttons">
                  <div 
                    className="id-upload-dropzone"
                    onClick={() => idFileInputRef.current?.click()}
                  >
                    {formData.idDocument ? (
                      <div className="id-preview">
                        <CheckCircle size={24} className="success-icon" />
                        <span>{formData.idDocument.name}</span>
                      </div>
                    ) : (
                      <>
                        <Upload size={24} />
                        <span>Gallery</span>
                      </>
                    )}
                  </div>
                  <div 
                    className="id-upload-dropzone camera"
                    onClick={() => idCameraInputRef.current?.click()}
                  >
                    <Camera size={24} />
                    <span>Camera</span>
                  </div>
                </div>
                <input
                  ref={idFileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleIdUpload}
                  style={{ display: 'none' }}
                />
                <input
                  ref={idCameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleIdUpload}
                  style={{ display: 'none' }}
                />
                <p className="upload-hint">Driver&apos;s license, passport, or government ID</p>
              </div>

              {formData.idDocument && (
                <button 
                  className="btn-submit-id"
                  onClick={handleIdSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="spin" size={18} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Shield size={18} />
                      Submit for Verification
                    </>
                  )}
                </button>
              )}

              <p className="skip-hint">
                Verification helps build trust but you can skip for now
              </p>
            </div>
          )}

          {/* Terms Agreement Step */}
          {currentStepType === 'terms' && (
            <div className="step-content terms-step">
              <div className="terms-header">
                <FileText size={40} className="terms-icon" />
                <h2>Terms & Conditions</h2>
                <p className="step-subtitle">Please review and accept to continue</p>
              </div>

              <div className="terms-summary">
                <div className="terms-item">
                  <CheckCircle size={18} className="terms-check" />
                  <span>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a></span>
                </div>
                <div className="terms-item">
                  <CheckCircle size={18} className="terms-check" />
                  <span>I agree to the <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></span>
                </div>
                <div className="terms-item">
                  <CheckCircle size={18} className="terms-check" />
                  <span>I understand the <a href="/safety" target="_blank" rel="noopener noreferrer">Safety Guidelines</a></span>
                </div>
                <div className="terms-item">
                  <CheckCircle size={18} className="terms-check" />
                  <span>I accept the <a href="/payment-terms" target="_blank" rel="noopener noreferrer">Payment Terms</a></span>
                </div>
              </div>

              <label className="terms-checkbox">
                <input
                  type="checkbox"
                  checked={formData.termsAccepted}
                  onChange={e => setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">
                  I have read and agree to all terms and conditions
                </span>
              </label>
            </div>
          )}

          {/* Complete Step */}
          {currentStepType === 'complete' && (
            <div className="step-content complete-step">
              <div className="complete-icon">
                <Check size={48} />
              </div>
              <h2>You&apos;re All Set! 🎉</h2>
              <p className="step-subtitle">
                {showHeroSteps && showClientSteps
                  ? "You have full access to post tasks and earn money!"
                  : showHeroSteps 
                    ? "Your hero profile is ready. Start browsing jobs near you!"
                    : "You&apos;re ready to post your first task and get help!"}
              </p>

              <div className="complete-actions">
                <button className="btn-primary" onClick={handleComplete} disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="spin" size={18} />
                      Finishing...
                    </>
                  ) : (
                    showHeroSteps ? "Browse Jobs" : "Post a Task"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        {currentStepType !== 'complete' && (
          <div className="onboarding-nav">
            {currentStep > 1 && (
              <button className="btn-back" onClick={handleBack}>
                <ChevronLeft size={18} />
                Back
              </button>
            )}
            
            <div className="nav-right">
              {/* Show skip on skippable steps */}
              {(currentStepType === 'payouts' || currentStepType === 'verify_id') && (
                <button className="btn-skip" onClick={handleSkip}>
                  Skip for Now
                </button>
              )}
              
              {/* Show continue button for most steps (not payouts, verify_id, or terms) */}
              {currentStepType !== 'payouts' && currentStepType !== 'verify_id' && currentStepType !== 'terms' && (
                <button 
                  className="btn-next" 
                  onClick={handleNext} 
                  disabled={saving || (currentStepType === 'welcome' && !formData.selectedRole)}
                >
                  {saving ? (
                    <>
                      <Loader2 className="spin" size={18} />
                      Saving...
                    </>
                  ) : currentStepType === 'welcome' ? (
                    <>
                      Get Started
                      <ChevronRight size={18} />
                    </>
                  ) : (
                    <>
                      Continue
                      <ChevronRight size={18} />
                    </>
                  )}
                </button>
              )}
              
              {/* Terms step - Accept button */}
              {currentStepType === 'terms' && (
                <button 
                  className="btn-next btn-accept-terms"
                  onClick={handleAcceptTerms}
                  disabled={!formData.termsAccepted || saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="spin" size={18} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Accept & Continue
                    </>
                  )}
                </button>
              )}
              
              {/* For verify_id step, show submit button if document uploaded */}
              {currentStepType === 'verify_id' && formData.idDocument && (
                <button 
                  className="btn-next"
                  onClick={handleIdSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="spin" size={18} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      Submit & Continue
                      <ChevronRight size={18} />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingWizard;
